import { FormEvent, useMemo } from 'react';
import { Loader2, Network, Printer } from 'lucide-react';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';
import { RestaurantPrinterForm } from '../../../types/restaurant/RestaurantPrinterTypes';

interface Props {
  isOpen: boolean;
  form: RestaurantPrinterForm;
  saving: boolean;
  onClose: () => void;
  onChange: <K extends keyof RestaurantPrinterForm>(
    field: K,
    value: RestaurantPrinterForm[K]
  ) => void;
  onSave: () => void;
}

const RestaurantPrinterFormModal = ({
  isOpen,
  form,
  saving,
  onClose,
  onChange,
  onSave,
}: Props) => {
  const errors = useMemo(() => {
    const next: Partial<Record<keyof RestaurantPrinterForm, string>> = {};
    if (!form.name.trim()) next.name = 'El nombre interno es obligatorio.';
    if (form.name.trim().length > 100)
      next.name = 'El nombre no puede superar 100 caracteres.';
    if (!form.printerName.trim())
      next.printerName = 'El nombre de la impresora es obligatorio.';
    if (form.printerName.trim().length > 200)
      next.printerName = 'El nombre de la impresora no puede superar 200 caracteres.';
    if (form.connectionType === 'NETWORK') {
      if (!form.ipAddress.trim()) next.ipAddress = 'Ingrese la dirección IP.';
      if (form.ipAddress.trim().length > 100)
        next.ipAddress = 'La dirección no puede superar 100 caracteres.';
      if (!form.port || Number(form.port) < 1 || Number(form.port) > 65535)
        next.port = 'El puerto debe estar entre 1 y 65535.';
    }
    return next;
  }, [form]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (Object.keys(errors).length === 0) onSave();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={form.id ? 'Editar impresora del restaurante' : 'Nueva impresora del restaurante'}
      maxWidth="max-w-3xl"
      headerExtra={<RestaurantFormHelp topic="printer" />}
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="rounded-lg border border-primary/15 bg-primary/5 p-4">
          <div className="flex gap-3">
            <Printer className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="font-medium text-black dark:text-white">
                Identificación del equipo
              </p>
              <p className="mt-1 text-sm text-bodydark2 dark:text-bodydark">
                El nombre interno ayuda a ubicarla; el nombre del sistema debe coincidir exactamente con el equipo instalado.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputField
            label="Nombre interno"
            name="name"
            type="text"
            iconKey="printer"
            value={form.name}
            max={100}
            required
            error={errors.name}
            placeholder="Ej. Cocina principal"
            onChange={(event) => onChange('name', event.target.value)}
          />
          <InputField
            label="Nombre de la impresora"
            name="printerName"
            type="text"
            iconKey="printer"
            value={form.printerName}
            required
            error={errors.printerName}
            placeholder="Ej. EPSON-TM-T20III"
            onChange={(event) => onChange('printerName', event.target.value)}
          />
          <SelectField
            label="Uso de la impresora"
            name="printerType"
            value={form.printerType}
            required
            iconKey="printer"
            options={[
              { value: 'KITCHEN', label: 'Cocina' },
              { value: 'RECEIPT', label: 'Recibos' },
              { value: 'LABEL', label: 'Etiquetas' },
            ]}
            onChange={(event) =>
              onChange('printerType', event.target.value as RestaurantPrinterForm['printerType'])
            }
          />
          <SelectField
            label="Tipo de conexión"
            name="connectionType"
            value={form.connectionType}
            required
            iconKey="network"
            options={[
              { value: 'LOCAL', label: 'Local / USB' },
              { value: 'NETWORK', label: 'Red' },
            ]}
            onChange={(event) =>
              onChange('connectionType', event.target.value as RestaurantPrinterForm['connectionType'])
            }
          />
        </div>

        {form.connectionType === 'NETWORK' && (
          <div className="rounded-lg border border-stroke p-4 dark:border-strokedark">
            <div className="mb-4 flex items-center gap-2 text-sm font-medium text-black dark:text-white">
              <Network className="h-4 w-4 text-primary" />
              Conexión de red
            </div>
            <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
              <InputField
                label="Dirección IP o host"
                name="ipAddress"
                type="text"
                iconKey="network"
                value={form.ipAddress}
                required
                error={errors.ipAddress}
                placeholder="192.168.1.50"
                onChange={(event) => onChange('ipAddress', event.target.value)}
              />
              <InputField
                label="Puerto"
                name="port"
                type="number"
                iconKey="port"
                value={form.port}
                min={1}
                max={65535}
                required
                error={errors.port}
                onChange={(event) =>
                  onChange('port', event.target.value ? Number(event.target.value) : '')
                }
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
          <InputField
            label="Orden de visualización"
            name="displayOrder"
            type="number"
            iconKey="list"
            value={form.displayOrder}
            min={0}
            onChange={(event) => onChange('displayOrder', Math.max(0, Number(event.target.value)))}
          />
          <div className="mb-4 rounded-lg border border-stroke px-4 py-3 dark:border-strokedark">
            <ToggleSwitch
              label="Disponible para imprimir"
              name="restaurantPrinterEnabled"
              checked={form.enabled}
              onChange={() => onChange('enabled', !form.enabled)}
            />
            <p className="mt-2 text-xs text-bodydark2 dark:text-bodydark">
              Puede guardar el equipo sin habilitarlo mientras termina su instalación.
            </p>
          </div>
          {form.id && (
            <div className="mb-4 rounded-lg border border-stroke px-4 py-3 dark:border-strokedark md:col-span-2">
              <ToggleSwitch
                label="Registro activo"
                name="restaurantPrinterActive"
                checked={form.active ?? true}
                onChange={() => onChange('active', !(form.active ?? true))}
              />
              <p className="mt-2 text-xs text-bodydark2 dark:text-bodydark">
                Reactive el registro para que pueda volver a asignarse dentro de la sucursal.
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="mb-3 block text-black dark:text-white">Descripción</label>
          <textarea
            name="description"
            value={form.description}
            maxLength={300}
            rows={3}
            placeholder="Ubicación física o indicaciones para el personal..."
            onChange={(event) => onChange('description', event.target.value)}
            className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
          />
          <p className="mt-1 text-right text-xs text-bodydark2">{form.description.length}/300</p>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-5 dark:border-strokedark sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded border border-stroke px-5 py-2.5 font-medium text-black transition hover:bg-gray-2 disabled:opacity-60 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || Object.keys(errors).length > 0}
            className="inline-flex items-center justify-center gap-2 rounded bg-primary px-5 py-2.5 font-medium text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Guardando...' : form.id ? 'Guardar cambios' : 'Registrar impresora'}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default RestaurantPrinterFormModal;
